import kyWithSentry from './helper/kyWithSentry';

import getGPSListUrl from 'url-constructors/getGPSListUrl';
import getCustomListUrl from 'url-constructors/getCustomListUrl';
import {
  ICustomList,
  IGetCustomLists,
} from 'components/customlists/AddToList/shared';
import getCustomListsURL from 'url-constructors/getCustomListsURL';
import getCustomListsByIdURL from 'url-constructors/getCustomListsByIdURL';

export interface IListNotFound {
  code: number;
  message: string;
  id: string;
}

export interface ILlistOflistsInterceptorResult {
  listOfLists: ICustomList[];
  notFoundLists: IListNotFound[];
  warnings: string[];
}

const getGpsList = async (): Promise<ICustomList> => {
  const gpsListUrl = getGPSListUrl();

  const gps = await kyWithSentry.get(gpsListUrl).json<{ list_id: string }>();

  return await kyWithSentry
    .get(
      getCustomListUrl({
        listId: gps.list_id,
        expand: 'permissions',
      })
    )
    .json<ICustomList>();
};

const getCuratedLists = async ({
  sortBy,
  sortOrder,
}: {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}): Promise<ICustomList[]> => {
  const customListsUrl = getCustomListsURL({
    sortBy,
    sortOrder,
  });

  const curatedListResult = await kyWithSentry
    .get(customListsUrl)
    .json<IGetCustomLists>();

  return curatedListResult.lists;
};

type TMyLists = ICustomList | IListNotFound;

const getMyLists = async (listIds: string[]): Promise<TMyLists[]> => {
  const customListsByIdUrl = getCustomListsByIdURL();
  return await kyWithSentry
    .post(customListsByIdUrl, { json: listIds })
    .json<TMyLists[]>();
};

const sortByHandler = ({
  sortBy,
  sortOrder,
}: {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}): any => {
  return (a: any, b: any): any => {
    if (a[sortBy] < b[sortBy]) {
      return sortOrder === 'desc' ? 1 : -1;
    }
    if (a[sortBy] > b[sortBy]) {
      return sortOrder === 'asc' ? 1 : -1;
    }
    return 0;
  };
};

const listOflistsInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  const { myListIds, isExternalUser, sortBy, sortOrder } = interceptionData;
  const listOfLists: ICustomList[] = [];

  try {
    const promises: [
      Promise<ICustomList>,
      Promise<ICustomList[]>,
      Promise<TMyLists[]>
    ] = [
      !isExternalUser
        ? getGpsList()
        : new Promise<ICustomList>((resolve) => resolve()),
      getCuratedLists({ sortBy, sortOrder }),
      myListIds?.length
        ? getMyLists(myListIds)
        : new Promise<any[]>((resolve) => resolve([])),
    ];

    const [gpsList, curatedLists, myLists] = await Promise.allSettled(promises);

    const gpsListResponse =
      gpsList.status === 'fulfilled' ? gpsList.value : null;
    const curatedListsResponse =
      curatedLists.status === 'fulfilled' ? curatedLists.value : [];
    const myListsResponse = myLists.status === 'fulfilled' ? myLists.value : [];

    const gpsId = gpsListResponse?.id;

    if (gpsListResponse) {
      listOfLists.push(gpsListResponse);
    }

    const sortedLists: any = [
      ...curatedListsResponse.filter((item: ICustomList) => item.id !== gpsId),
      ...(myListsResponse.filter((item: TMyLists) => {
        return !('code' in item) && item.id !== gpsId;
      }) as ICustomList[]),
    ];

    listOfLists.push(...sortedLists.sort(sortByHandler({ sortBy, sortOrder })));

    const notFoundLists = myListsResponse.filter((item: TMyLists) => {
      return 'code' in item;
    }) as IListNotFound[];

    return new Response(
      JSON.stringify({
        listOfLists,
        notFoundLists,
        warnings: undefined,
      })
    );
  } catch (error) {
    return new Response(
      JSON.stringify({
        listOfLists: undefined,
        notFoundLists: undefined,
        warnings: ['There was an error loading the custom lists.'],
      })
    );
  }
};

export default listOflistsInterceptor;
